import React from 'react'
import { connect } from 'react-redux'

import { Entity } from '../../app/entity'

import Chart from '../histogram.d3'

import { HistogramView } from '../../domain/HistogramView'
import BoxBody from '../../helper/boxBody'
import LargeNumber from '../../helper/large-number'
import Loading from '../../helper/loading'
import Milliseconds from '../../helper/milliseconds'
import { ApplicationState } from '../../redux/ApplicationState'
import { newResizeObserver } from '../../helper/resizeObserver'

interface Props {
    loading: boolean
    data: { report: HistogramView }
}
export class HistogramComponent extends React.Component<Props> {
    public static displayName = 'Histogram'

    public chart
    public svg
    public resizeObserver

    public createChart() {
        this.chart = new Chart()

        const chartElement = this.getChartElement()

        if (!chartElement) {
            return
        }

        const parentNode = jQuery(chartElement).parent()
        this.chart.create(chartElement, parentNode.width(), 180, this.tickFormatterCallBack)
        this.updateChart()
    }

    public updateChart = () => {
        if (!this.props?.data?.report || !Array.isArray(this.props.data.report.buckets)) {
            return
        }

        if (this.props.data.report.buckets.length === 0) {
            return
        }

        const markers: Array<{ start: number; end: number; type: string }> = []
        if (this.props.data.report && this.props.data.report.markers) {
            const marker95 = this.props.data.report.markers.find(marker => marker.name === '95%')
            const marker99 = this.props.data.report.markers.find(marker => marker.name === '99%')
            const markerMax = this.props.data.report.markers.find(marker => marker.name === 'Max')
            const markerTimeExceeded = this.props.data.report.markers.find(marker => marker.name === 'timeExceeded')

            const lastBucket = this.props.data.report.buckets[this.props.data.report.buckets.length - 1]

            if (marker95 && marker99 && markerMax) {
                markers.push({
                    start: 0,
                    end: marker95.value,
                    type: 'success',
                })

                if (marker95.value !== marker99.value) {
                    markers.push({
                        start: marker95.value,
                        end: marker99.value,
                        type: 'warning',
                    })
                }
                if (marker99.value !== markerMax.value) {
                    markers.push({
                        start: marker99.value,
                        end: markerMax.value,
                        type: 'danger',
                    })
                }
            }

            if (markerTimeExceeded) {
                markers.push({
                    start: lastBucket.start + 7,
                    end: lastBucket.end + 7,
                    type: 'timeExceeded',
                })
            }

            // make sure last marker stretches until the end
            markers[markers.length - 1] = { ...markers[markers.length - 1], end: lastBucket.end + 7 }
        }

        this.chart.update(
            [
                this.props.data.report.buckets.map(bucket => ({
                    x: new Date(bucket.start),
                    y: bucket.value,
                    end: new Date(bucket.end),
                })),
            ],
            markers,
            this.tickFormatterCallBack,
            this.tooltipMessageCallBack,
        )
    }

    public setResizeObserver = () => {
        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()
            this.resizeObserver = newResizeObserver(this.rebuildChart)
            this.resizeObserver.observe(parentNode[0])
        }
    }

    public rebuildChart = () => {
        this.destroyChart()
        this.createChart()
        if (this.chart) this.updateChart()
    }

    private destroyChart() {
        this.chart?.destroy() // remove svg-elements from dom
        this.chart = null // remove reference to the object
    }

    public componentDidMount() {
        if (!this.chart) {
            this.createChart()
        }
    }

    public componentDidUpdate(prevProps, prevState) {
        if (!this.chart) {
            return
        }

        if (!this.resizeObserver && this.chart) {
            this.setResizeObserver()
        }

        this.updateChart()
    }

    public componentWillUnmount() {
        this.chart?.destroy()
    }

    public getChartElement = () => {
        return this.svg
    }

    public render() {
        if (!this.props.data) {
            return <Loading {...this.props} height={135} title="Response Time Distribution" />
        }

        return (
            <div className="box box-default">
                <div className="box-header">
                    <nav className="navbar navbar-toolbar">
                        <div className="container-fluid">
                            <h3 className="box-title">Response Time Distribution</h3>

                            {this.props.data.report.markers ? (
                                <ul className="nav navbar-nav navbar-right navbar-numbers">
                                    {this.props.data.report.markers.map(marker => {
                                        return (
                                            <li key={marker.name}>
                                                <a>
                                                    <Milliseconds value={marker.value * 1000} /> <small>{marker.name}</small>
                                                </a>
                                            </li>
                                        )
                                    })}
                                    <li>
                                        <a>
                                            <LargeNumber value={this.props.data.report.total} /> <small>Requests</small>
                                        </a>
                                    </li>
                                </ul>
                            ) : null}
                        </div>
                    </nav>
                </div>

                <BoxBody {...this.props}>
                    <div className="chart">
                        <svg ref={svg => (this.svg = svg)} />
                    </div>
                </BoxBody>
            </div>
        )
    }

    private tickFormatterCallBack = (d: Date) => {
        return d.getTime() + 'ms'
    }
    private tooltipMessageCallBack = (x: Date, end: Date) => {
        const tooltipTimeText =
            x.getTime() < 60000 ? this.tickFormatterCallBack(x) + ' - ' + this.tickFormatterCallBack(end) : 'Response Time'
        const timeExceededText = x.getTime() < 60000 ? '' : '60+ s'
        return [tooltipTimeText, timeExceededText]
    }
}

export default connect<Props, {}, {}>((globalState: ApplicationState, props) => {
    const histogram = globalState.operations.histogram[globalState.tidewaysContext.route.parameters.operation] || new Entity()

    return {
        ...histogram,
    }
})(HistogramComponent)
